/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2024
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useState } from 'react';
import { DataContextProvider } from '@forge/data-connector';
import { Button, Card, Icon, Tooltip } from '@scuf/common';
import { useTranslation } from '@scuf/localization';
import _ from 'lodash';

import IEnergyManagementProps, {
  IAny,
  IEmaasEnergyOptimizationComponentData
} from '../../../IEnergyManagementProps';
import descriptors from '../../../../../locales/en-US/descriptors';
import EnergyOptimizationComponent from '../EnergyOptimizationComponent';
import {
  EOUtilityTabNameEnum,
  getDescriptorForEOUtilityTabNameEnum
} from './EODetailsByUtilityUtils';
import { TextTabbedSwitch } from '../../../../shared/TabbedSwitch/TextTabbedSwitch/TextTabbedSwitch';

import '../EOtheme-combined.scss';
import './EOByUtilitySiteWidgetTheme.scss';
import { ApiContext } from '../../../../../context/ApiContext';
import EnergyOptimizationUtilityComponent from '../EnergyOptimizationComponentForUtility';
import {
  getEmsopStrategyStatus,
  getEoLastStatus,
  getEOLastTooltip,
  getStrategyLastUpdated
} from '../../../../shared/Utils/EnergyOptimizationUtils';
import EnergyOptimizationStrategyStatus from '../EnergyOptimizationStrategyStatus';

interface IEmaasEOByUtilitySiteWidget extends IEnergyManagementProps {
  mainGraph?: boolean;
}

const EOByUtilitySiteWidget = (props: IEmaasEOByUtilitySiteWidget) => {
  const {
    bearerToken,
    requestContext,
    connectorConfig,
    mainGraph,
    triggerDateChange
  } = props;
  const [t, lng] = useTranslation();
  const d: IAny = descriptors.energyManagement;

  const apiContext = useContext(ApiContext);
  const { eoAvailableTabs } = apiContext;
  const [data, setData] = useState<any>(
    {} as IEmaasEnergyOptimizationComponentData
  );
  const [eoOperationData, setEoOperationData] = useState(
    {} as IEmaasEnergyOptimizationComponentData
  );

  const [currentTab, setCurrentTab] = useState<EOUtilityTabNameEnum>(
    EOUtilityTabNameEnum.All
  );

  const renderEOComponent = (activeTab: string) => {
    switch (activeTab) {
      case EOUtilityTabNameEnum.Electricity:
        return (
          <EnergyOptimizationUtilityComponent
            requestContext={requestContext}
            bearerToken={bearerToken}
            mainGraph={false}
            utilityType={EOUtilityTabNameEnum.Electricity.toString()}
            eoOperationData={eoOperationData}
            setEoOperationData={setEoOperationData}
          />
        );
      case EOUtilityTabNameEnum.Gas:
        return (
          <EnergyOptimizationUtilityComponent
            requestContext={requestContext}
            bearerToken={bearerToken}
            mainGraph={false}
            utilityType={EOUtilityTabNameEnum.Gas.toString()}
            triggerDateChange={triggerDateChange}
            eoOperationData={eoOperationData}
            setEoOperationData={setEoOperationData}
          />
        );
      case EOUtilityTabNameEnum.Steam:
        return (
          <EnergyOptimizationUtilityComponent
            requestContext={requestContext}
            bearerToken={bearerToken}
            mainGraph={false}
            utilityType={EOUtilityTabNameEnum.Steam.toString()}
            eoOperationData={eoOperationData}
            setEoOperationData={setEoOperationData}
          />
        );
      case EOUtilityTabNameEnum.HotWater:
        return (
          <EnergyOptimizationUtilityComponent
            requestContext={requestContext}
            bearerToken={bearerToken}
            mainGraph={false}
            utilityType={EOUtilityTabNameEnum.HotWater.toString()}
            eoOperationData={eoOperationData}
            setEoOperationData={setEoOperationData}
          />
        );
      case EOUtilityTabNameEnum.ChilledWater:
        return (
          <EnergyOptimizationUtilityComponent
            requestContext={requestContext}
            bearerToken={bearerToken}
            mainGraph={false}
            utilityType={EOUtilityTabNameEnum.ChilledWater.toString()}
            eoOperationData={eoOperationData}
            setEoOperationData={setEoOperationData}
          />
        );
      default:
        return (
          <EnergyOptimizationComponent
            requestContext={requestContext}
            bearerToken={bearerToken}
            mainGraph={false}
            saveDataInSite={setData}
            isAllUtility={true}
            setEoOperationData={setEoOperationData}
          />
        );
    }
  };

  const availableTabs = [...(eoAvailableTabs || [])];
  const emsopStrategyStatus = getEmsopStrategyStatus(data);

  const strategyVal = getStrategyLastUpdated(data);
  const strategyLastUpdated =
    strategyVal != undefined
      ? strategyVal.locale(lng).format('DD MMM YYYY h:mm a')
      : undefined;

  return (
    <DataContextProvider config={connectorConfig}>
      <div
        className={
          apiContext.toggleEnableEoStatus
            ? 'energy-optimization-widget energy-optimization-widget-with-status kpi-card-common eo-by-utility-with-status'
            : 'energy-optimization-widget kpi-card-common eo-by-utility'
        }
      >
        <Card className="eo-by-utility-card">
          <Card.Header className={`kpi-header-container eo-with-utility-tabs`}>
            <div className="kpi-card-title-EO">
              {t(d.ENERGYOPTIMIZATIONSAVINGS)}
            </div>
            {availableTabs.length > 0 && (
              <TextTabbedSwitch
                tabList={_.map(availableTabs, (tabName) => ({
                  id: tabName.toString(),
                  label: t(
                    getDescriptorForEOUtilityTabNameEnum(
                      EOUtilityTabNameEnum[
                        tabName as keyof typeof EOUtilityTabNameEnum
                      ]
                    )
                  )
                }))}
                onTabChange={(id: string) => {
                  setCurrentTab(
                    EOUtilityTabNameEnum[
                      id as keyof typeof EOUtilityTabNameEnum
                    ]
                  );
                }}
                activeTab={currentTab.toString()}
              >
                {!apiContext.toggleEnableEoStatus &&
                  emsopStrategyStatus !== undefined && (
                    <div className="energy-optimization-header-title">
                      <EnergyOptimizationStrategyStatus
                        strategyStatus={emsopStrategyStatus}
                        strategyLastUpdated={strategyLastUpdated}
                      />
                      <Tooltip
                        element={
                          <Icon root="common" name="badge-info" size="small" />
                        }
                        event="hover"
                        content={t(d.EMAASEOSTATETOOLTIP)}
                        position="right center"
                        size="small"
                      />
                    </div>
                  )}
              </TextTabbedSwitch>
            )}
          </Card.Header>
          {renderEOComponent(currentTab)}
        </Card>
      </div>
    </DataContextProvider>
  );
};
export default EOByUtilitySiteWidget;
